// useCrypto — React hook around the crypto surface of matrix.ts.
//
// Responsibilities:
//   - Subscribe to incoming verification requests.
//   - Track the currently-active verification session (incoming or
//     outgoing) and its state transitions.
//   - Expose imperative helpers the dialog / banner call.
//
// This hook never imports from the SDK directly — everything goes
// through `matrix.ts`. That contract is what lets us swap the stub for
// the WASM bindings without touching the React tree.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  startVerificationRequest,
  subscribeVerificationRequests,
  type Client,
  type QrVerificationSession,
  type Unsubscribe,
  type VerificationRequest,
  type VerificationSession,
  type VerificationState,
} from "~/lib/matrix";
import { recordSdkBreadcrumb } from "~/lib/telemetry";

const STALE_VERIFICATION_ERROR = "verification request is not known";
const STALE_VERIFICATION_MESSAGE = "This verification request expired. Start a new one.";

// A late duplicate of an earlier phase must not regress an advanced request
// snapshot (it would re-gate `startSas`); terminal states always apply.
const VERIFICATION_PHASE_RANK: Record<VerificationState, number> = {
  requested: 0,
  ready: 1,
  accepted: 2,
  done: 3,
  cancelled: 4,
  failed: 4,
};

export interface UseCryptoResult {
  /** The most recent incoming request that hasn't been acted on. */
  incomingRequest: VerificationRequest | null;
  /** The active verification session, once accepted / started. */
  session: VerificationSession | null;
  request: VerificationRequest | null;
  qrSession: QrVerificationSession | null;
  verificationError: string | null;
  /** Current state of `session`, or `null` if none. */
  sessionState: VerificationState | null;
  qrSessionState: VerificationState | null;
  completedVerification: {
    token: number;
    method: "emoji" | "qr";
    client: Client;
    userId: string;
  } | null;
  /** Accept the current incoming request. Promotes it to `session`. */
  acceptIncoming: () => Promise<void>;
  /** Decline the current incoming request. Clears the banner. */
  declineIncoming: () => Promise<void>;
  /** Dismiss the banner without cancelling the request. */
  dismissIncoming: () => void;
  /** Confirm "they match" on the active session. */
  confirmMatch: () => Promise<void>;
  startSas: () => Promise<void>;
  startQr: () => Promise<void>;
  scanQrCode: (bytes: Uint8Array) => Promise<void>;
  confirmQr: () => Promise<void>;
  /** Cancel the active session. */
  cancelSession: () => Promise<void>;
  /** Start an outgoing verification. Dialog callers pass the target
   *  device they intend to verify. */
  startVerification: (userId: string, deviceId: string) => Promise<void>;
  /** Clear the active session, typically after it terminates and the
   *  dialog closes. */
  clearSession: () => void;
}

interface IdentityState<T> {
  userId: string;
  value: T;
}

interface SessionIdentityState extends IdentityState<VerificationSession> {
  client: Client;
  lifecycle: number;
}

interface RequestIdentityState extends IdentityState<VerificationRequest> {
  client: Client;
  lifecycle: number;
}

interface VerificationCallIdentity {
  userId: string;
  client?: Client;
  lifecycle?: number;
}

interface QrSessionIdentityState extends IdentityState<QrVerificationSession> {
  client: Client;
  lifecycle: number;
}

/**
 * Hook binding verification state to a signed-in identity. Pass `null`
 * for either input before login — the hook is a no-op in that branch so
 * the tree can mount before crypto is ready.
 */
export function useCrypto(client: Client | null, userId: string | null): UseCryptoResult {
  const [incomingRequestIdentityState, setIncomingRequestIdentityState] =
    useState<IdentityState<VerificationRequest> | null>(null);
  const [sessionIdentityState, setSessionIdentityState] = useState<SessionIdentityState | null>(
    null,
  );
  const [requestIdentityState, setRequestIdentityState] = useState<RequestIdentityState | null>(
    null,
  );
  const [qrSessionIdentityState, setQrSessionIdentityState] =
    useState<QrSessionIdentityState | null>(null);
  const [sessionState, setSessionState] = useState<VerificationState | null>(null);
  const [qrSessionState, setQrSessionState] = useState<VerificationState | null>(null);
  const [, setSessionRevision] = useState(0);
  const [completedVerification, setCompletedVerification] = useState<{
    token: number;
    method: "emoji" | "qr";
    client: Client;
    userId: string;
  } | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const stateUnsubRef = useRef<Unsubscribe | null>(null);
  const qrStateUnsubRef = useRef<Unsubscribe | null>(null);
  const lifecycleRef = useRef(0);
  const identitySnapshotRef = useRef<{ client: Client | null; userId: string | null }>({
    client,
    userId,
  });
  const currentClientRef = useRef<Client | null>(client);
  const currentUserIdRef = useRef<string | null>(userId);
  const hasActiveVerificationRef = useRef(false);
  const activeRequestIdRef = useRef<string | null>(null);
  const acceptedRequestKeysRef = useRef(new Set<string>());
  const adoptedPeerSessionKeysRef = useRef(new Set<string>());
  const completionTokenRef = useRef(0);
  if (
    identitySnapshotRef.current.client !== client ||
    identitySnapshotRef.current.userId !== userId
  ) {
    lifecycleRef.current += 1;
    identitySnapshotRef.current = { client, userId };
  }
  currentClientRef.current = client;
  currentUserIdRef.current = userId;

  const incomingRequest =
    incomingRequestIdentityState?.userId === userId ? incomingRequestIdentityState.value : null;
  const activeSessionIdentityState =
    sessionIdentityState?.userId === userId && sessionIdentityState.client === client
      ? sessionIdentityState
      : null;
  const session = activeSessionIdentityState?.value ?? null;
  const activeRequestIdentityState =
    requestIdentityState?.userId === userId && requestIdentityState.client === client
      ? requestIdentityState
      : null;
  const request = activeRequestIdentityState?.value ?? null;
  const activeQrSessionIdentityState =
    qrSessionIdentityState?.userId === userId && qrSessionIdentityState.client === client
      ? qrSessionIdentityState
      : null;
  const qrSession = activeQrSessionIdentityState?.value ?? null;
  hasActiveVerificationRef.current = Boolean(request || session || qrSession);
  activeRequestIdRef.current = request?.id ?? null;

  const clearActiveVerification = useCallback((sessionUserId: string) => {
    setSessionIdentityState((current) => (current?.userId === sessionUserId ? null : current));
    setRequestIdentityState((current) => (current?.userId === sessionUserId ? null : current));
    setQrSessionIdentityState((current) => (current?.userId === sessionUserId ? null : current));
  }, []);

  // Subscribe to incoming verification requests. A second request
  // while one is already pending replaces the pending one — two
  // simultaneous banners would be noisy and only the newer request
  // represents the user's current reality.
  //
  // A client or identity swap invalidates any pending request or
  // session from the previous identity — they belong to a user who no
  // longer owns this tab. Cleanup clears both so the new session starts
  // silent.
  useEffect(() => {
    if (!client || !userId) {
      setIncomingRequestIdentityState(null);
      setSessionIdentityState(null);
      setRequestIdentityState(null);
      setQrSessionIdentityState(null);
      setVerificationError(null);
      setCompletedVerification(null);
      completionTokenRef.current = 0;
      return;
    }
    const unsub = subscribeVerificationRequests(client, (req) => {
      setRequestIdentityState((current) => {
        if (
          current?.userId !== userId ||
          current.client !== client ||
          current.value.id !== req.id
        ) {
          return current;
        }
        if (VERIFICATION_PHASE_RANK[req.state] < VERIFICATION_PHASE_RANK[current.value.state]) {
          return current;
        }
        return { ...current, value: req };
      });
      if (activeRequestIdRef.current === req.id) return;
      if (acceptedRequestKeysRef.current.has(`${userId}\u0000${req.id}`)) return;
      // A finished request (our own cancelled one included) is not an offer;
      // holding it as "incoming" would keep the recovery sheet hidden.
      if (VERIFICATION_PHASE_RANK[req.state] >= VERIFICATION_PHASE_RANK.done) {
        setIncomingRequestIdentityState((current) =>
          current?.value.id === req.id ? null : current,
        );
        return;
      }
      if (hasActiveVerificationRef.current) clearActiveVerification(userId);
      setIncomingRequestIdentityState({ userId, value: req });
    });
    return () => {
      lifecycleRef.current += 1;
      unsub();
      setIncomingRequestIdentityState((current) => (current?.userId === userId ? null : current));
      setSessionIdentityState((current) => (current?.userId === userId ? null : current));
      setRequestIdentityState((current) => (current?.userId === userId ? null : current));
      setQrSessionIdentityState((current) => (current?.userId === userId ? null : current));
      setVerificationError(null);
      setCompletedVerification((current) => (current?.userId === userId ? null : current));
      completionTokenRef.current = 0;
    };
  }, [clearActiveVerification, client, userId]);

  const handleVerificationCallError = useCallback(
    (action: string, error: unknown, identity: VerificationCallIdentity): boolean => {
      const message = error instanceof Error ? error.message : String(error);
      if (!message.includes(STALE_VERIFICATION_ERROR)) return false;
      recordSdkBreadcrumb("matrix.verification", "Matrix verification request expired", error, {
        action,
      });
      if (
        currentUserIdRef.current !== identity.userId ||
        (identity.client && currentClientRef.current !== identity.client) ||
        (identity.lifecycle !== undefined && lifecycleRef.current !== identity.lifecycle)
      ) {
        return true;
      }
      clearActiveVerification(identity.userId);
      setVerificationError(STALE_VERIFICATION_MESSAGE);
      return true;
    },
    [clearActiveVerification],
  );

  // Keep sessionState synced with the active session. A change of
  // session tears down the previous subscription — multiple
  // subscribers to one session would each receive the same update,
  // and the stale ones would fight the live UI state.
  useEffect(() => {
    const sessionIdentity = activeSessionIdentityState;
    if (stateUnsubRef.current) {
      stateUnsubRef.current();
      stateUnsubRef.current = null;
    }
    if (!session || !sessionIdentity) {
      setSessionState(null);
      return;
    }
    stateUnsubRef.current = session.onState((s) => {
      setSessionRevision((current) => current + 1);
      setSessionState(s);
      if (s === "done") {
        completionTokenRef.current += 1;
        setCompletedVerification({
          token: completionTokenRef.current,
          method: "emoji",
          client: sessionIdentity.client,
          userId: sessionIdentity.userId,
        });
      }
    });
    return () => {
      if (stateUnsubRef.current) {
        stateUnsubRef.current();
        stateUnsubRef.current = null;
      }
    };
  }, [activeSessionIdentityState, session]);

  useEffect(() => {
    const qrIdentity = activeQrSessionIdentityState;
    if (qrStateUnsubRef.current) {
      qrStateUnsubRef.current();
      qrStateUnsubRef.current = null;
    }
    if (!qrSession || !qrIdentity) {
      setQrSessionState(null);
      return;
    }
    qrStateUnsubRef.current = qrSession.onState((s) => {
      setSessionRevision((current) => current + 1);
      setQrSessionState(s);
      if (s === "done") {
        completionTokenRef.current += 1;
        setCompletedVerification({
          token: completionTokenRef.current,
          method: "qr",
          client: qrIdentity.client,
          userId: qrIdentity.userId,
        });
      }
    });
    return () => {
      if (qrStateUnsubRef.current) {
        qrStateUnsubRef.current();
        qrStateUnsubRef.current = null;
      }
    };
  }, [activeQrSessionIdentityState, qrSession]);

  // A peer-initiated flow has no `startSas`/`startQr` click to promote it, so
  // adopt the surfaced session into state directly (guarded like `startSas`).
  useEffect(() => {
    const reqIdentity = activeRequestIdentityState;
    if (!reqIdentity) return;
    const {
      value: req,
      client: reqClient,
      userId: reqUserId,
      lifecycle: reqLifecycle,
    } = reqIdentity;
    let cancelled = false;

    const adopt = async <T extends VerificationSession | QrVerificationSession>(
      method: "sas" | "qr",
      load: () => Promise<T | null>,
      setState: (next: { userId: string; client: Client; lifecycle: number; value: T }) => void,
    ): Promise<void> => {
      const key = `${reqUserId}\u0000${req.id}\u0000${method}`;
      if (adoptedPeerSessionKeysRef.current.has(key)) return;
      adoptedPeerSessionKeysRef.current.add(key);
      let live: T | null;
      try {
        live = await load();
      } catch (error) {
        adoptedPeerSessionKeysRef.current.delete(key);
        if (
          handleVerificationCallError(`adoptPeerSession:${method}`, error, {
            userId: reqUserId,
            client: reqClient,
            lifecycle: reqLifecycle,
          })
        ) {
          return;
        }
        // No caller awaits this effect, so a surfaced session evicted between
        // the binding emit and this read must not escape as an unhandled
        // rejection — record it and leave the request for a later emit.
        recordSdkBreadcrumb(
          "matrix.verification",
          "Matrix peer verification session adoption failed",
          error,
          { method },
        );
        return;
      }
      if (!live) {
        adoptedPeerSessionKeysRef.current.delete(key);
        return;
      }
      if (
        cancelled ||
        lifecycleRef.current !== reqLifecycle ||
        currentClientRef.current !== reqClient ||
        currentUserIdRef.current !== reqUserId
      ) {
        await live.cancel();
        return;
      }
      setState({ userId: reqUserId, client: reqClient, lifecycle: reqLifecycle, value: live });
    };

    if (!session) void adopt("sas", () => req.peerSasSession(), setSessionIdentityState);
    if (!qrSession) void adopt("qr", () => req.peerQrSession(), setQrSessionIdentityState);

    return () => {
      cancelled = true;
    };
  }, [activeRequestIdentityState, session, qrSession, handleVerificationCallError]);

  const acceptIncoming = useCallback(async () => {
    const req = incomingRequest;
    const requestClient = client;
    const requestUserId = userId;
    const requestLifecycle = lifecycleRef.current;
    if (
      !req ||
      !requestClient ||
      !requestUserId ||
      lifecycleRef.current !== requestLifecycle ||
      currentClientRef.current !== requestClient ||
      currentUserIdRef.current !== requestUserId
    ) {
      return;
    }
    const requestKey = `${requestUserId}\u0000${req.id}`;
    acceptedRequestKeysRef.current.add(requestKey);
    let acceptedRequest: VerificationRequest;
    try {
      acceptedRequest = await req.accept();
    } catch (error) {
      acceptedRequestKeysRef.current.delete(requestKey);
      if (
        handleVerificationCallError("acceptVerificationRequest", error, {
          userId: requestUserId,
          client: requestClient,
          lifecycle: requestLifecycle,
        })
      ) {
        setIncomingRequestIdentityState((current) =>
          current?.userId === requestUserId ? null : current,
        );
        return;
      }
      throw error;
    }
    if (
      lifecycleRef.current !== requestLifecycle ||
      currentClientRef.current !== requestClient ||
      currentUserIdRef.current !== requestUserId
    ) {
      await req.cancel();
      return;
    }
    setRequestIdentityState({
      userId: requestUserId,
      client: requestClient,
      lifecycle: requestLifecycle,
      value: acceptedRequest,
    });
    setIncomingRequestIdentityState((current) =>
      current?.userId === requestUserId ? null : current,
    );
    setVerificationError(null);
  }, [client, handleVerificationCallError, incomingRequest, userId]);

  const startSas = useCallback(async () => {
    const activeRequest = request;
    const requestClient = activeRequestIdentityState?.client;
    const requestUserId = activeRequestIdentityState?.userId;
    const requestLifecycle = activeRequestIdentityState?.lifecycle;
    if (!activeRequest || !requestClient || !requestUserId || requestLifecycle === undefined)
      return;
    if (activeRequest.state !== "ready") throw new Error("Emoji verification is not ready yet.");
    let live: VerificationSession | null;
    try {
      live = await activeRequest.startSas();
    } catch (error) {
      if (
        handleVerificationCallError("startSas", error, {
          userId: requestUserId,
          client: requestClient,
          lifecycle: requestLifecycle,
        })
      )
        return;
      throw error;
    }
    if (!live) throw new Error("Emoji verification is not ready yet.");
    if (
      lifecycleRef.current !== requestLifecycle ||
      currentClientRef.current !== requestClient ||
      currentUserIdRef.current !== requestUserId
    ) {
      await live.cancel();
      return;
    }
    setSessionIdentityState({
      userId: requestUserId,
      client: requestClient,
      lifecycle: requestLifecycle,
      value: live,
    });
  }, [activeRequestIdentityState, handleVerificationCallError, request]);

  const startQr = useCallback(async () => {
    const activeRequest = request;
    const requestClient = activeRequestIdentityState?.client;
    const requestUserId = activeRequestIdentityState?.userId;
    const requestLifecycle = activeRequestIdentityState?.lifecycle;
    if (!activeRequest || !requestClient || !requestUserId || requestLifecycle === undefined)
      return;
    if (activeRequest.state !== "ready") throw new Error("QR verification is not ready yet.");
    let live: QrVerificationSession | null;
    try {
      live = await activeRequest.generateQrCode();
    } catch (error) {
      if (
        handleVerificationCallError("generateQrCode", error, {
          userId: requestUserId,
          client: requestClient,
          lifecycle: requestLifecycle,
        })
      )
        return;
      throw error;
    }
    if (!live) throw new Error("QR verification is not ready yet.");
    if (
      lifecycleRef.current !== requestLifecycle ||
      currentClientRef.current !== requestClient ||
      currentUserIdRef.current !== requestUserId
    ) {
      await live.cancel();
      return;
    }
    setQrSessionIdentityState({
      userId: requestUserId,
      client: requestClient,
      lifecycle: requestLifecycle,
      value: live,
    });
  }, [activeRequestIdentityState, handleVerificationCallError, request]);

  const scanQrCode = useCallback(
    async (bytes: Uint8Array) => {
      const activeRequest = request;
      const requestClient = activeRequestIdentityState?.client;
      const requestUserId = activeRequestIdentityState?.userId;
      const requestLifecycle = activeRequestIdentityState?.lifecycle;
      if (!activeRequest || !requestClient || !requestUserId || requestLifecycle === undefined)
        return;
      if (activeRequest.state !== "ready") {
        throw new Error("That QR code is not ready for this verification request.");
      }
      let live: QrVerificationSession | null;
      try {
        live = await activeRequest.scanQrCode(bytes);
      } catch (error) {
        if (
          handleVerificationCallError("scanQrCode", error, {
            userId: requestUserId,
            client: requestClient,
            lifecycle: requestLifecycle,
          })
        )
          return;
        throw error;
      }
      if (!live) throw new Error("That QR code is not ready for this verification request.");
      if (
        lifecycleRef.current !== requestLifecycle ||
        currentClientRef.current !== requestClient ||
        currentUserIdRef.current !== requestUserId
      ) {
        await live.cancel();
        return;
      }
      setQrSessionIdentityState({
        userId: requestUserId,
        client: requestClient,
        lifecycle: requestLifecycle,
        value: live,
      });
    },
    [activeRequestIdentityState, handleVerificationCallError, request],
  );

  const declineIncoming = useCallback(async () => {
    const req = incomingRequest;
    const requestUserId = userId;
    if (!req || !requestUserId || currentUserIdRef.current !== requestUserId) return;
    try {
      await req.cancel();
    } catch (error) {
      if (
        !handleVerificationCallError("declineVerificationRequest", error, {
          userId: requestUserId,
        })
      ) {
        throw error;
      }
    }
    if (currentUserIdRef.current !== requestUserId) return;
    setIncomingRequestIdentityState((current) =>
      current?.userId === requestUserId ? null : current,
    );
  }, [handleVerificationCallError, incomingRequest, userId]);

  const dismissIncoming = useCallback(() => {
    const requestUserId = userId;
    if (!requestUserId || currentUserIdRef.current !== requestUserId) return;
    setIncomingRequestIdentityState((current) =>
      current?.userId === requestUserId ? null : current,
    );
  }, [userId]);

  const confirmMatch = useCallback(async () => {
    const sessionClient = activeSessionIdentityState?.client;
    const sessionUserId = activeSessionIdentityState?.userId;
    const sessionLifecycle = activeSessionIdentityState?.lifecycle;
    if (
      !session ||
      !sessionClient ||
      !sessionUserId ||
      sessionLifecycle === undefined ||
      lifecycleRef.current !== sessionLifecycle ||
      currentClientRef.current !== sessionClient ||
      currentUserIdRef.current !== sessionUserId
    ) {
      return;
    }
    await session.confirm();
  }, [activeSessionIdentityState, session]);

  const confirmQr = useCallback(async () => {
    const live = qrSession;
    if (!live) return;
    await live.confirm();
  }, [qrSession]);

  const cancelSession = useCallback(async () => {
    const activeIdentity =
      activeSessionIdentityState ?? activeQrSessionIdentityState ?? activeRequestIdentityState;
    const sessionClient = activeIdentity?.client;
    const sessionUserId = activeIdentity?.userId;
    const sessionLifecycle = activeIdentity?.lifecycle;
    const liveQr = qrSession;
    if (
      (!session && !request && !liveQr) ||
      !sessionClient ||
      !sessionUserId ||
      sessionLifecycle === undefined ||
      lifecycleRef.current !== sessionLifecycle ||
      currentClientRef.current !== sessionClient ||
      currentUserIdRef.current !== sessionUserId
    ) {
      return;
    }
    const cancellations: Promise<void>[] = [];
    if (session) cancellations.push(session.cancel());
    if (liveQr) cancellations.push(liveQr.cancel());
    if (request) cancellations.push(request.cancel());
    const results = await Promise.allSettled(cancellations);
    const failed = results.find(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    );
    if (failed) {
      if (
        handleVerificationCallError("cancelVerificationRequest", failed.reason, {
          userId: sessionUserId,
          client: sessionClient,
          lifecycle: sessionLifecycle,
        })
      ) {
        return;
      }
      throw failed.reason;
    }
  }, [
    activeQrSessionIdentityState,
    activeRequestIdentityState,
    activeSessionIdentityState,
    handleVerificationCallError,
    qrSession,
    request,
    session,
  ]);

  const startVerification = useCallback(
    async (targetUserId: string, deviceId: string) => {
      const sessionUserId = userId;
      const sessionClient = client;
      const sessionLifecycle = lifecycleRef.current;
      if (
        !sessionClient ||
        !sessionUserId ||
        currentClientRef.current !== sessionClient ||
        currentUserIdRef.current !== sessionUserId
      ) {
        return;
      }
      const liveRequest = await startVerificationRequest(sessionClient, targetUserId, deviceId);
      if (
        lifecycleRef.current !== sessionLifecycle ||
        currentClientRef.current !== sessionClient ||
        currentUserIdRef.current !== sessionUserId
      ) {
        await liveRequest.cancel();
        return;
      }
      setRequestIdentityState({
        userId: sessionUserId,
        client: sessionClient,
        lifecycle: sessionLifecycle,
        value: liveRequest,
      });
      setVerificationError(null);
    },
    [client, userId],
  );

  const clearSession = useCallback(() => {
    const sessionUserId = userId;
    if (!sessionUserId || currentUserIdRef.current !== sessionUserId) return;
    clearActiveVerification(sessionUserId);
    setVerificationError(null);
  }, [clearActiveVerification, userId]);

  return {
    incomingRequest,
    request,
    session,
    qrSession,
    verificationError,
    sessionState,
    qrSessionState,
    completedVerification,
    acceptIncoming,
    declineIncoming,
    dismissIncoming,
    confirmMatch,
    startSas,
    startQr,
    scanQrCode,
    confirmQr,
    cancelSession,
    startVerification,
    clearSession,
  };
}
